/**
 * Project a REAL Atlas Discovery snapshot (apps/atlas-discovery/out/campaign/<domain>/snapshot.json) into
 * the Command Center. Two consumers:
 *
 *   1. projectSnapshot() → normalized `AtlasDataset` (entities/evidence/relationships) that feeds the
 *      generic ENVIRONMENT + RELATIONSHIPS tabs and the evidence drawer.
 *   2. deriveDiscovery{Summary,Intelligence,Executive}() → discovery-NATIVE read models for TODAY, the
 *      intelligence cards, and EXECUTIVE.
 *
 * Evidence-first discipline (directive):
 *   - Every value traces to a field the discovery collector actually observed — nothing is synthesized.
 *   - PROJECTION is allowed, NEW INFERENCE is not. The `security` and `business.{risk,operational}` blocks
 *     are already computed by the Atlas Discovery engine (each item carries a `basis` provenance string);
 *     we surface them verbatim. We never invent a severity, a risk, or a conclusion the engine didn't make.
 *   - A discovery brief observes the PUBLIC surface only. It does NOT compute severity-ranked findings,
 *     cross-run history/diff, or replay/impact. Those stay explicitly unavailable, never faked.
 *   - Attribution: a snapshot observes an EXTERNAL domain, not the operator's own environment.
 */

import type { AtlasDataset, Entity, Evidence, Relationship } from "./model";
import { available, unavailable, type Availability } from "./datasource";

/* ----------------------------------------------------------------------------------------------- *
 * Real snapshot shape (a superset of the fields the Command Center consumes; extra fields ignored).
 * ----------------------------------------------------------------------------------------------- */
export interface RawObservation {
  readonly status?: string;
  readonly value?: unknown;
  readonly source?: string;
  readonly at?: string;
  readonly detail?: string;
}
export interface DiscoverySnapshot {
  readonly domain: string;
  readonly generatedAt: string;
  readonly disclaimer?: string;
  readonly organization?: { readonly primaryDomain?: string; readonly nameservers?: readonly string[]; readonly mailProvider?: string };
  readonly domains?: readonly string[];
  readonly ipRanges?: readonly { readonly ip: string; readonly ptr?: string; readonly asn?: string | number; readonly prefix?: string; readonly org?: string; readonly cc?: string }[];
  readonly technologies?: readonly string[];
  readonly publicServices?: readonly string[];
  readonly security?: {
    readonly tls?: { readonly present?: boolean; readonly summary?: string; readonly valid?: boolean; readonly daysToExpiry?: number };
    readonly email?: Record<string, string>;
    readonly certificateHealth?: string;
    readonly publicExposure?: string;
  };
  readonly business?: {
    readonly costOptimization?: readonly { readonly text: string; readonly basis: string }[];
    readonly operational?: readonly { readonly text: string; readonly basis: string }[];
    readonly risk?: readonly { readonly text: string; readonly basis: string }[];
  };
  readonly coverage?: { readonly checks?: number; readonly observed?: number };
  readonly raw?: Record<string, unknown>;
}

/* ----------------------------------------------------------------------------------------------- *
 * (1) Normalized dataset — entities / evidence / relationships.
 * ----------------------------------------------------------------------------------------------- */
export interface SnapshotProjection {
  readonly dataset: AtlasDataset;
  readonly observedTargetLabel: string;
}

export function projectSnapshot(s: DiscoverySnapshot): SnapshotProjection {
  const at = s.generatedAt;
  const entities: Entity[] = [];
  const evidence: Evidence[] = [];
  const relationships: Relationship[] = [];
  let ev = 0;
  const addEvidence = (subjectId: string, observation: Record<string, unknown>, verified: boolean, basis?: string): void => {
    evidence.push({
      id: `disc.ev.${s.domain}.${ev++}`,
      subjectId,
      subjectType: "entity",
      kind: "api_response",
      source: "collector:discovery",
      collectedAt: at,
      verification: verified ? "verified" : "unverified",
      confidence: verified ? 0.9 : 0.6,
      content: observation,
      metadata: { collector: "discovery", target: s.domain, basis },
    });
  };

  const orgId = `org:${s.domain}`;
  entities.push({ id: orgId, type: "organization", name: s.organization?.primaryDomain ?? s.domain, displayName: s.domain, status: "unknown", metadata: { mailProvider: s.organization?.mailProvider, nameservers: s.organization?.nameservers }, tags: ["discovery"], createdAt: at, updatedAt: at });
  addEvidence(orgId, { primaryDomain: s.organization?.primaryDomain, mailProvider: s.organization?.mailProvider, nameservers: s.organization?.nameservers }, Boolean(s.organization?.primaryDomain), "dns:NS/MX");

  for (const d of s.domains ?? []) {
    const id = `domain:${d}`;
    entities.push({ id, type: "domain", name: d, status: "unknown", metadata: {}, tags: ["discovery"], createdAt: at, updatedAt: at });
    relationships.push({ id: `rel:${orgId}->${id}`, from: orgId, to: id, type: "owns", metadata: { observed: "same organization footprint" } });
    addEvidence(id, { observedDomain: d }, true, "dns:A");
  }

  for (const r of s.ipRanges ?? []) {
    const id = `network:${r.ip}`;
    entities.push({ id, type: "network", name: r.ip, displayName: r.ptr ?? r.ip, status: "unknown", metadata: { ptr: r.ptr, asn: r.asn, prefix: r.prefix, org: r.org, cc: r.cc }, tags: ["discovery"], createdAt: at, updatedAt: at });
    relationships.push({ id: `rel:${orgId}->${id}`, from: orgId, to: id, type: "owns", metadata: { observed: "resolves to" } });
    addEvidence(id, { ip: r.ip, ptr: r.ptr, asn: r.asn, prefix: r.prefix, org: r.org, cc: r.cc }, true, "cymru:asn");
  }

  (s.publicServices ?? []).forEach((svc, i) => {
    const id = `service:${s.domain}:${i}`;
    entities.push({ id, type: "service", name: svc, status: "unknown", metadata: { observation: svc }, tags: ["discovery", "exposure"], createdAt: at, updatedAt: at });
    relationships.push({ id: `rel:${orgId}->exposes->${id}`, from: orgId, to: id, type: "exposes", metadata: { observed: "reachable public service" } });
    addEvidence(id, { publicService: svc }, true, "http");
  });

  (s.technologies ?? []).forEach((tech, i) => {
    const id = `app:${s.domain}:${i}`;
    entities.push({ id, type: "application", name: tech, status: "unknown", metadata: { observation: tech }, tags: ["discovery", "technology"], createdAt: at, updatedAt: at });
    relationships.push({ id: `rel:${orgId}->uses->${id}`, from: orgId, to: id, type: "uses", metadata: { observed: "fingerprinted technology" } });
    addEvidence(id, { technology: tech }, true, "http/dns fingerprint");
  });

  if (s.security?.tls) addEvidence(orgId, { tls: s.security.tls }, Boolean(s.security.tls.valid), "tls:443");
  if (s.security?.email) addEvidence(orgId, { email: s.security.email }, true, "dns:TXT");
  if (s.coverage) addEvidence(orgId, { coverage: s.coverage }, true, "collector:coverage");

  const dataset: AtlasDataset = {
    runId: `discovery:${s.domain}:${at}`,
    observedAt: at,
    entities,
    evidence,
    relationships,
    // findings/changes intentionally UNSET — a discovery brief does not compute them (honest unavailable).
  };
  return { dataset, observedTargetLabel: `${s.domain} · observed via Atlas Discovery ${at.slice(0, 10)}` };
}

export function snapshotDataSource(s: DiscoverySnapshot): { load(): Promise<AtlasDataset>; label: string } {
  const { dataset, observedTargetLabel } = projectSnapshot(s);
  return { load: () => Promise.resolve(dataset), label: observedTargetLabel };
}

/* ----------------------------------------------------------------------------------------------- *
 * (2a) TODAY summary — discovery-NATIVE metrics. Deliberately NO "assets" counter: the dashboard
 *      adapts to what a discovery observation actually knows (directive), it does not borrow the
 *      assessment concept of an asset inventory.
 * ----------------------------------------------------------------------------------------------- */
export interface DiscoverySummary {
  readonly domain: string;
  readonly observedAt: string;
  readonly domainsObserved: number;
  readonly ipRangesObserved: number;
  readonly publicServicesObserved: number;
  readonly technologiesObserved: number;
  readonly securityObservations: number;
  /** Observed / attempted checks — genuinely present on the snapshot, else honestly unavailable. */
  readonly coverage: Availability<{ readonly observed: number; readonly checks: number }>;
}

/** Count of distinct security posture facts the collector actually observed (never a fabricated score). */
export function countSecurityObservations(s: DiscoverySnapshot): number {
  let n = 0;
  const sec = s.security ?? {};
  if (sec.tls?.present) n++;
  if (sec.certificateHealth) n++;
  if (sec.publicExposure) n++;
  for (const k of Object.keys(sec.email ?? {})) {
    const v = sec.email?.[k];
    if (v && v !== "Not observed") n++;
  }
  return n;
}

export function deriveDiscoverySummary(s: DiscoverySnapshot): DiscoverySummary {
  const cov = s.coverage;
  return {
    domain: s.domain,
    observedAt: s.generatedAt,
    domainsObserved: (s.domains ?? []).length,
    ipRangesObserved: (s.ipRanges ?? []).length,
    publicServicesObserved: (s.publicServices ?? []).length,
    technologiesObserved: (s.technologies ?? []).length,
    securityObservations: countSecurityObservations(s),
    coverage: typeof cov?.observed === "number" && typeof cov?.checks === "number" ? available({ observed: cov.observed, checks: cov.checks }) : unavailable("coverage not reported by this observation"),
  };
}

/* ----------------------------------------------------------------------------------------------- *
 * (2b) Intelligence cards — "Atlas already looked; here is what deserves attention." A PROJECTION of
 *      the engine's own security posture + business.{risk,operational} items. Each card carries the
 *      engine's `basis` as provenance and inline evidence. No history exists for a single snapshot, so
 *      no History/Replay action is ever offered (directive).
 * ----------------------------------------------------------------------------------------------- */
export interface DiscoveryEvidence {
  readonly source: string; // provenance / basis, e.g. "tls:443", "http:GET (…)", "dns:TXT(spf)"
  readonly observation: string;
  readonly observedAt: string;
  readonly verification: "verified" | "unverified";
}
export interface DiscoveryIntelligence {
  readonly id: string;
  readonly kind: "risk" | "posture" | "operational";
  /** Attention items are the engine-flagged risks; everything else is observed context. */
  readonly attention: boolean;
  readonly title: string;
  readonly detail: string;
  readonly basis: string;
  /** Entity ids this observation concerns (for the Relationships hop). Org-scoped for posture. */
  readonly relatedEntityIds: readonly string[];
  readonly evidence: readonly DiscoveryEvidence[];
  /** Discovery is a single observation — no prior snapshot to diff, so history is never offered. */
  readonly historyAvailable: false;
}

/** Map an engine `basis` string to the raw provenance entry that backs it, when present. */
function rawProvenance(s: DiscoverySnapshot, basis: string): DiscoveryEvidence | null {
  const raw = s.raw ?? {};
  const dns = (raw.dns ?? {}) as Record<string, RawObservation>;
  const pick = (o: RawObservation | undefined, fallbackSource: string): DiscoveryEvidence | null =>
    o && o.status === "observed"
      ? { source: o.source ?? fallbackSource, observation: typeof o.value === "object" ? JSON.stringify(o.value).slice(0, 400) : String(o.value ?? o.detail ?? ""), observedAt: o.at ?? s.generatedAt, verification: "verified" }
      : null;
  if (basis.startsWith("tls")) return pick(raw.tls as RawObservation, "tls:443");
  if (basis.startsWith("http")) return pick(raw.http as RawObservation, "http:GET");
  if (basis.startsWith("cymru")) return pick(raw.ips as RawObservation, "cymru:asn");
  if (basis.includes("spf") || basis.includes("dmarc") || basis.startsWith("dns:TXT")) return pick(dns.txt, "dns:TXT");
  if (basis.startsWith("dns:MX")) return pick(dns.mx, "dns:MX");
  if (basis.startsWith("dns")) return pick(dns.a, "dns:A");
  return null;
}

export function deriveDiscoveryIntelligence(s: DiscoverySnapshot): readonly DiscoveryIntelligence[] {
  const orgId = `org:${s.domain}`;
  const out: DiscoveryIntelligence[] = [];
  let i = 0;
  const evFor = (basis: string, observation: string, verified: boolean): DiscoveryEvidence[] => {
    const primary: DiscoveryEvidence = { source: basis, observation, observedAt: s.generatedAt, verification: verified ? "verified" : "unverified" };
    const raw = rawProvenance(s, basis);
    return raw ? [primary, raw] : [primary];
  };

  // What deserves attention: the engine's own risk observations (already computed; we do not add any).
  for (const r of s.business?.risk ?? []) {
    out.push({ id: `intel:risk:${i++}`, kind: "risk", attention: true, title: "Deserves attention", detail: r.text, basis: r.basis, relatedEntityIds: [orgId], evidence: evFor(r.basis, r.text, false), historyAvailable: false });
  }

  // Security posture (observed facts, surfaced as context — not re-scored into new risks).
  const tls = s.security?.tls;
  if (tls?.present) {
    const detail = s.security?.certificateHealth ?? tls.summary ?? "TLS observed";
    out.push({ id: `intel:tls:${i++}`, kind: "posture", attention: false, title: "TLS / certificate", detail, basis: "tls:443", relatedEntityIds: [orgId], evidence: evFor("tls:443", tls.summary ?? detail, Boolean(tls.valid)), historyAvailable: false });
  }
  const email = s.security?.email ?? {};
  const emailParts = Object.entries(email).filter(([, v]) => v && v !== "Not observed").map(([k, v]) => `${k.toUpperCase()}: ${v}`);
  if (emailParts.length) {
    out.push({ id: `intel:email:${i++}`, kind: "posture", attention: false, title: "Email authentication", detail: emailParts.join(" · "), basis: "dns:TXT(spf/dmarc), mta-sts", relatedEntityIds: [orgId], evidence: evFor("dns:TXT", emailParts.join(" · "), true), historyAvailable: false });
  }
  if (s.security?.publicExposure) {
    out.push({ id: `intel:exposure:${i++}`, kind: "posture", attention: false, title: "Public exposure", detail: s.security.publicExposure, basis: "http", relatedEntityIds: [orgId], evidence: evFor("http", s.security.publicExposure, false), historyAvailable: false });
  }

  // Operational context (engine-produced, informational).
  for (const o of s.business?.operational ?? []) {
    out.push({ id: `intel:ops:${i++}`, kind: "operational", attention: false, title: "Operational", detail: o.text, basis: o.basis, relatedEntityIds: [orgId], evidence: evFor(o.basis, o.text, true), historyAvailable: false });
  }
  return out;
}

/* ----------------------------------------------------------------------------------------------- *
 * (2c) EXECUTIVE — a deterministic, evidence-backed interpretation. Every line is derived from the
 *      snapshot; the "cannot yet determine" section is a product strength, not a gap to paper over.
 * ----------------------------------------------------------------------------------------------- */
export interface DiscoveryExecutive {
  readonly domain: string;
  readonly observedAt: string;
  readonly whatObserved: readonly string[];
  readonly whatDeservesAttention: readonly { readonly text: string; readonly basis: string }[];
  readonly whatIsVerified: readonly string[];
  readonly whatCannotYetDetermine: readonly string[];
  readonly recommendedNextStep: string;
}

export function deriveDiscoveryExecutive(s: DiscoverySnapshot): DiscoveryExecutive {
  const sum = deriveDiscoverySummary(s);
  const observed: string[] = [];
  if (sum.domainsObserved) observed.push(`${sum.domainsObserved} domain${sum.domainsObserved === 1 ? "" : "s"}`);
  if (sum.ipRangesObserved) {
    const hosters = [...new Set((s.ipRanges ?? []).map((r) => r.org).filter(Boolean))];
    observed.push(`${sum.ipRangesObserved} public IP${sum.ipRangesObserved === 1 ? "" : "s"}${hosters.length ? ` (${hosters.slice(0, 2).join(", ")})` : ""}`);
  }
  if (sum.publicServicesObserved) observed.push(`${sum.publicServicesObserved} public service${sum.publicServicesObserved === 1 ? "" : "s"}`);
  if (sum.technologiesObserved) observed.push(`${sum.technologiesObserved} technolog${sum.technologiesObserved === 1 ? "y" : "ies"}`);
  if (s.organization?.mailProvider) observed.push(`email on ${s.organization.mailProvider}`);

  const verified: string[] = [];
  if (s.security?.tls?.valid) verified.push(s.security.certificateHealth ?? `TLS certificate valid (${s.security.tls.daysToExpiry ?? "?"} days to expiry)`);
  const email = s.security?.email ?? {};
  if (email.spf && email.spf !== "Not observed") verified.push(`SPF ${email.spf.toLowerCase()}`);
  if (email.dmarc && email.dmarc !== "Not observed") verified.push(`DMARC ${email.dmarc.toLowerCase()}`);

  const cannot = [
    "Internal dependency and network-reachability structure — not observable from the public surface.",
    "Business impact / blast radius of any observation — requires an authorized assessment.",
    "Actual cost figures — not publicly observable (Atlas quantifies these against real inventory under assessment).",
    "Change history — this is a single observation; there is no prior snapshot to diff against yet.",
  ];

  const attention = (s.business?.risk ?? []).map((r) => ({ text: r.text, basis: r.basis }));
  const recommendedNextStep = attention.length
    ? `Atlas has observed the public surface of ${s.domain} and flagged ${attention.length} item${attention.length === 1 ? "" : "s"} that deserve attention. Internal dependency and impact information is not available from this observation — run an authorized Atlas assessment to prove internal posture and quantify impact.`
    : `Atlas has observed the public surface of ${s.domain} and found no public-posture risks. Internal dependency and impact information is not available from this observation — run an authorized Atlas assessment to prove internal posture and quantify impact.`;

  return { domain: s.domain, observedAt: s.generatedAt, whatObserved: observed, whatDeservesAttention: attention, whatIsVerified: verified, whatCannotYetDetermine: cannot, recommendedNextStep };
}
